/**
 * api.js — the single channel between the browser and the CloudIQ Studio API.
 *
 * What changed and why
 * --------------------
 * The previous client sent no `Authorization` header on any of its ~25 calls,
 * had no 401 handling (a session expiry surfaced as the string "API error 401"
 * inside a red box), no request timeout (a hung backend spun the loader
 * forever), and a latent bug where `...options` was spread *after* `headers`, so
 * any caller passing its own headers silently dropped `Content-Type`.
 *
 * It also hardcoded `'/api'`, so a build could not be pointed at a different
 * host without editing source.
 *
 * Design notes
 * ------------
 * *Token storage.* The session token lives in `sessionStorage`, not
 * `localStorage`: it is scoped to the tab and cleared when the tab closes, which
 * limits the window in which a token left on a shared machine is usable. Both
 * are readable by injected script, so the real mitigations are the short
 * server-side TTL, the idle timeout and immediate revocation on logout.
 *
 * *401 handling.* A single `onUnauthorized` subscriber is notified whenever the
 * server rejects a token, so the app can clear its session and show the sign-in
 * screen from one place instead of every component interpreting the error text.
 */

const BASE = (import.meta.env?.VITE_API_BASE_URL ?? '/api').replace(/\/+$/, '')

const TOKEN_KEY = 'cloudiq.session.token'
const USER_KEY = 'cloudiq.session.user'

/** Requests are abandoned after this long so a stalled backend cannot hang the UI. */
const DEFAULT_TIMEOUT_MS = 45_000

/** Report generation performs many sequential AWS calls and multi-file telemetry parsing, so it gets a longer budget (10 min). */
const LONG_TIMEOUT_MS = 600_000

// ── Session storage ──────────────────────────────────────────────────────────

export function getToken() {
  try {
    return sessionStorage.getItem(TOKEN_KEY)
  } catch {
    // Private-browsing modes can throw on storage access.
    return null
  }
}

export function getStoredUser() {
  try {
    const raw = sessionStorage.getItem(USER_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export function setSession(token, user) {
  try {
    sessionStorage.setItem(TOKEN_KEY, token)
    sessionStorage.setItem(USER_KEY, JSON.stringify(user ?? null))
  } catch {
    // Storage unavailable: the in-memory session still works for this page view.
  }
}

export function clearSession() {
  try {
    sessionStorage.removeItem(TOKEN_KEY)
    sessionStorage.removeItem(USER_KEY)
  } catch {
    /* nothing recoverable */
  }
}

let unauthorizedHandler = null

/**
 * Register the single callback invoked when the server rejects our token.
 * Returns an unsubscribe function.
 */
export function onUnauthorized(handler) {
  unauthorizedHandler = handler
  return () => {
    if (unauthorizedHandler === handler) unauthorizedHandler = null
  }
}

// ── Errors ───────────────────────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(message, { status = 0, code = '', detail = null } = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.detail = detail
  }

  get isAuthError() {
    return this.status === 401
  }

  get isForbidden() {
    return this.status === 403
  }

  /** True when retrying the same request could plausibly succeed. */
  get isRetryable() {
    return this.status === 0 || this.status === 429 || this.status >= 500
  }
}

/**
 * Turn a failed response into an ApiError with a message fit for a user.
 *
 * FastAPI puts validation failures in `detail` as an array of objects; rendering
 * that array directly produced "[object Object]" in the old client.
 */
async function toApiError(response) {
  let detail = null
  let message = ''

  try {
    const body = await response.json()
    detail = body?.detail ?? body
    if (typeof detail === 'string') {
      message = detail
    } else if (Array.isArray(detail)) {
      message = detail
        .map((item) => {
          const field = Array.isArray(item?.loc) ? item.loc.filter((p) => p !== 'body').join('.') : ''
          return field ? `${field}: ${item?.msg ?? 'is invalid'}` : (item?.msg ?? 'Invalid input')
        })
        .join('; ')
    } else if (detail && typeof detail === 'object') {
      message = detail.message || JSON.stringify(detail)
    }
  } catch {
    /* response had no JSON body */
  }

  if (!message) {
    const fallbacks = {
      401: 'Your session has expired. Sign in again to continue.',
      403: 'You do not have permission to perform this action.',
      404: 'The requested item no longer exists.',
      413: 'That file is too large to upload.',
      429: 'Too many requests. Wait a moment and try again.',
      502: 'The platform could not reach an upstream service.',
      503: 'That feature is not configured on this deployment.',
    }
    message = fallbacks[response.status] || `The server returned an unexpected error (${response.status}).`
  }

  return new ApiError(message, { status: response.status, detail })
}

// ── Core request ─────────────────────────────────────────────────────────────

async function request(path, { method = 'GET', body, timeoutMs = DEFAULT_TIMEOUT_MS, signal } = {}) {
  const token = getToken()
  const headers = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (token) headers.Authorization = `Bearer ${token}`

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  // Honour a caller-supplied signal as well as our timeout.
  if (signal) signal.addEventListener('abort', () => controller.abort(), { once: true })

  let response
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
    })
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw new ApiError(
        signal?.aborted
          ? 'The request was cancelled.'
          : `The request timed out after ${Math.round(timeoutMs / 1000)} seconds.`,
        { status: 0, code: 'timeout' },
      )
    }
    throw new ApiError('Could not reach the CloudIQ Studio API. Check your connection.', {
      status: 0,
      code: 'network',
    })
  } finally {
    clearTimeout(timer)
  }

  if (response.status === 401) {
    clearSession()
    unauthorizedHandler?.()
    throw await toApiError(response)
  }
  if (!response.ok) throw await toApiError(response)
  if (response.status === 204) return null

  const contentType = response.headers.get('content-type') || ''
  return contentType.includes('application/json') ? response.json() : response.text()
}

// ── Authentication ───────────────────────────────────────────────────────────

export const login = (email, password) =>
  request('/auth/login', { method: 'POST', body: { email, password } })

export const logout = () => request('/auth/logout', { method: 'POST' })

export const fetchCurrentUser = () => request('/auth/me')

export const changePassword = (currentPassword, newPassword) =>
  request('/auth/change-password', {
    method: 'POST',
    body: { current_password: currentPassword, new_password: newPassword },
  })

// ── Clients ──────────────────────────────────────────────────────────────────

export const getClients = () => request('/clients')

export const createClient = (payload) => request('/clients', { method: 'POST', body: payload })

export const updateClient = (clientId, payload) =>
  request(`/clients/${clientId}`, { method: 'PATCH', body: payload })

export const deleteClient = (clientId) => request(`/clients/${clientId}`, { method: 'DELETE' })

export const testConnection = (payload) =>
  request('/clients/test-connection', { method: 'POST', body: payload, timeoutMs: LONG_TIMEOUT_MS })

export const discoverResources = (clientId) =>
  request(`/clients/${clientId}/discover`, { method: 'POST', timeoutMs: LONG_TIMEOUT_MS })

export const getClientTelemetryProfile = (clientId) =>
  request(`/clients/${clientId}/telemetry-profile`, { timeoutMs: LONG_TIMEOUT_MS })

export const getAgentInstallCommand = (clientId) => request(`/clients/${clientId}/agent-command`)

/** The CloudFormation template is YAML, so it is fetched as text. */
export const getCloudFormationTemplate = (clientId) => request(`/clients/${clientId}/cloudformation`)

export const getClientWhitelist = (clientId) => request(`/clients/${clientId}/whitelist`)
export const updateClientWhitelist = (clientId, payload) =>
  request(`/clients/${clientId}/whitelist`, { method: 'PUT', body: payload })
export const getMyIp = () => request('/my-ip')
export const getNginxWhitelistSnippet = () => request('/pulse/nginx-whitelist')

// ── Analytics ────────────────────────────────────────────────────────────────

export const getClientCost = (clientId, { months = 3, refresh = false } = {}) =>
  request(`/clients/${clientId}/cost?months=${months}&refresh=${refresh}`, {
    timeoutMs: LONG_TIMEOUT_MS,
  })

export const getClientSla = (clientId, { days = 30, refresh = false } = {}) =>
  request(`/clients/${clientId}/sla?days=${days}&refresh=${refresh}`, {
    timeoutMs: LONG_TIMEOUT_MS,
  })

export const getPlatformRoi = () => request('/analytics/roi')

// ── Reports ──────────────────────────────────────────────────────────────────

export const getReports = (limit = 100) => request(`/reports?limit=${limit}`)

export const generateReport = (clientId, execMode = 'live_api') =>
  request(`/reports/generate/${clientId}`, {
    method: 'POST',
    body: { exec_mode: execMode },
    timeoutMs: LONG_TIMEOUT_MS,
  })

export const generateManualReport = (payload) =>
  request('/reports/generate-manual', {
    method: 'POST',
    body: payload,
    timeoutMs: LONG_TIMEOUT_MS,
  })

export const approveReport = (reportId) =>
  request(`/reports/${reportId}/approve`, { method: 'POST' })

export const dispatchReport = (reportId, recipients = []) =>
  request(`/reports/${reportId}/dispatch`, {
    method: 'POST',
    body: { recipients },
    timeoutMs: LONG_TIMEOUT_MS,
  })

/**
 * Download a generated artefact.
 *
 * Downloads cannot go through a plain `<a href>`: the route requires a bearer
 * token, which an anchor cannot send. The file is fetched as a blob and saved
 * via a temporary object URL. The previous client built a bare URL and clicked a
 * detached anchor, which would now return 401.
 */
export async function downloadReport(reportId, docType) {
  const token = getToken()
  const response = await fetch(`${BASE}/reports/${reportId}/download/${docType}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })

  if (response.status === 401) {
    clearSession()
    unauthorizedHandler?.()
    throw await toApiError(response)
  }
  if (!response.ok) throw await toApiError(response)

  const blob = await response.blob()
  const disposition = response.headers.get('content-disposition') || ''
  const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i)
  const filename = match ? decodeURIComponent(match[1]) : `cloudiq-report-${reportId}.${docType}`

  const url = URL.createObjectURL(blob)
  try {
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
  } finally {
    // Revoked on the next tick so the browser has started the save.
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }
  return filename
}

// ── Telemetry ingestion ──────────────────────────────────────────────────────

export const analyzeLogs = (rawLogs, clientName = 'Client infrastructure', instances = []) =>
  request('/analyze-logs', {
    method: 'POST',
    body: { raw_logs: rawLogs, client_name: clientName, instances },
    timeoutMs: LONG_TIMEOUT_MS,
  })

export const uploadScreenshot = (base64Data, filename) =>
  request('/upload/screenshot', {
    method: 'POST',
    body: { base64_data: base64Data, filename },
  })

/**
 * Upload a telemetry or log file.
 *
 * Uses FormData, so `Content-Type` is deliberately omitted: the browser must set
 * it with the multipart boundary.
 */
export async function uploadTelemetryFile(file, clientName = '') {
  const token = getToken()
  const formData = new FormData()
  formData.append('file', file)
  if (clientName) formData.append('client_name', clientName)

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), LONG_TIMEOUT_MS)

  let response
  try {
    response = await fetch(`${BASE}/upload-telemetry-file`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
      signal: controller.signal,
    })
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw new ApiError('The upload timed out.', { status: 0, code: 'timeout' })
    }
    throw new ApiError('Could not upload the file. Check your connection.', {
      status: 0,
      code: 'network',
    })
  } finally {
    clearTimeout(timer)
  }

  if (response.status === 401) {
    clearSession()
    unauthorizedHandler?.()
    throw await toApiError(response)
  }
  if (!response.ok) throw await toApiError(response)
  return response.json()
}

/**
 * Upload multiple telemetry or log files simultaneously and synthesize the full report schema.
 */
export async function uploadMultiTelemetryFiles(files, { clientName = '', accountId = '', monitoringPeriod = '', geminiApiKey = '' } = {}) {
  const token = getToken()
  const formData = new FormData()
  let totalBytes = 0
  for (const file of files) {
    formData.append('files', file)
    totalBytes += file.size || 0
  }
  if (clientName) formData.append('client_name', clientName)
  if (accountId) formData.append('account_id', accountId)
  if (monitoringPeriod) formData.append('monitoring_period', monitoringPeriod)
  if (geminiApiKey) formData.append('gemini_api_key', geminiApiKey)

  // Budget at least 10 minutes (600s), plus dynamic scaling for large multi-MB uploads
  const timeoutMs = Math.max(LONG_TIMEOUT_MS, Math.ceil(totalBytes / 1024) * 60)
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)

  let response
  try {
    response = await fetch(`${BASE}/upload-multi-telemetry-files`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
      signal: controller.signal,
    })
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw new ApiError('The upload timed out.', { status: 0, code: 'timeout' })
    }
    throw new ApiError('Could not upload files. Check your connection.', {
      status: 0,
      code: 'network',
    })
  } finally {
    clearTimeout(timer)
  }

  if (response.status === 401) {
    clearSession()
    unauthorizedHandler?.()
    throw await toApiError(response)
  }
  if (!response.ok) throw await toApiError(response)
  return response.json()
}

// ── Agents ───────────────────────────────────────────────────────────────────

export const getAgents = () => request('/agents')

// There is deliberately no "simulate agent heartbeat" helper. `/api/agent/telemetry`
// is an ingestion endpoint authenticated by an agent token, and calling it from the
// console with invented CPU/memory/disk values wrote fabricated measurements into
// the same table that feeds client-facing reports. Real heartbeats come only from
// an installed collector agent.

// ── Users ────────────────────────────────────────────────────────────────────

export const getUsers = () => request('/users')

export const createUser = (payload) => request('/users', { method: 'POST', body: payload })

export const updateUser = (userId, payload) =>
  request(`/users/${userId}`, { method: 'PUT', body: payload })

export const resetUserPassword = (userId, newPassword) =>
  request(`/users/${userId}/reset-password`, {
    method: 'POST',
    body: { new_password: newPassword },
  })

export const deleteUser = (userId) => request(`/users/${userId}`, { method: 'DELETE' })

// ── Security, audit and platform ─────────────────────────────────────────────

export const getAuditLogs = (limit = 100) => request(`/audit-logs?limit=${limit}`)

export const getSecurityPosture = () => request('/security/posture')

export const getPlatformSettings = () => request('/settings')

export const updatePlatformSettings = (payload) =>
  request('/settings', { method: 'PUT', body: payload })

export const getSchedulerStatus = () => request('/scheduler/status')

export const runScheduledReportNow = (clientId) =>
  request(`/scheduler/run-now/${clientId}`, { method: 'POST', timeoutMs: LONG_TIMEOUT_MS })

export const getHealthDetail = () => request('/health/detail')

// ── Database Backup & Disaster Recovery Replica ──────────────────────────────

export const getBackupStatus = () => request('/backup/status')

export const triggerBackup = () =>
  request('/backup/trigger', { method: 'POST', timeoutMs: LONG_TIMEOUT_MS })

// ── CloudIQ Pulse Telemetry & Alert Hub ──────────────────────────────────────

export const getPulseStatus = () => request('/pulse/status')
export const getPulseFleet = (accountId) => request(`/pulse/fleet/${accountId}`)
export const getPulseAnomalies = (accountId, minLevel = 5, limit = 100) =>
  request(`/pulse/anomalies/${accountId}?min_level=${minLevel}&limit=${limit}`)
export const getAlerts = (clientId) => request(clientId ? `/alerts?client_id=${clientId}` : '/alerts')
export const getAlertSettings = (clientId) =>
  request(clientId ? `/alerts/settings?client_id=${clientId}` : '/alerts/settings')
export const saveAlertSettings = (data) =>
  request('/alerts/settings', { method: 'POST', body: data })
export const testAlertWebhook = (webhookUrl) =>
  request('/alerts/test-webhook', { method: 'POST', body: { webhook_url: webhookUrl } })
export const testAlertEmail = (recipientEmails) =>
  request('/alerts/test-email', { method: 'POST', body: { recipient_emails: recipientEmails } })
export const acknowledgeIncident = (incidentId, note) =>
  request(`/alerts/acknowledge/${incidentId}`, { method: 'POST', body: { note } })

// ── Platform Alarms ─────────────────────────────────────────────────────────
export const getAlarms = (clientId) => request(clientId ? `/alarms?client_id=${clientId}` : '/alarms')
export const createAlarm = (data) => request('/alarms', { method: 'POST', body: data })
export const deleteAlarm = (alarmId) => request(`/alarms/${alarmId}`, { method: 'DELETE' })

// ── Application Catalog & 4-Layer Health Map ─────────────────────────────────
export const getApplications = (clientId) =>
  request(clientId ? `/applications?client_id=${clientId}` : '/applications')
export const getApplication = (endpointId) => request(`/applications/${endpointId}`)
export const createApplication = (data) => request('/applications', { method: 'POST', body: data })
export const updateApplication = (endpointId, data) =>
  request(`/applications/${endpointId}`, { method: 'PUT', body: data })
export const deleteApplication = (endpointId) =>
  request(`/applications/${endpointId}`, { method: 'DELETE' })
export const probeApplication = (endpointId) =>
  request(`/applications/${endpointId}/probe`, { method: 'POST' })
export const getApplicationHistory = (endpointId, limit = 50) =>
  request(`/applications/${endpointId}/history?limit=${limit}`)

// ── CloudIQ Shield: Security Posture & Vulnerability Scanner ─────────────────
export const getShieldVulnerabilities = (clientId, severity = null, limit = 150) => {
  const params = new URLSearchParams({ client_id: clientId, limit })
  if (severity) params.set('severity', severity)
  return request(`/shield/vulnerabilities?${params.toString()}`)
}

export const getShieldCompliance = (clientId) =>
  request(`/shield/compliance?client_id=${clientId}`)

export const getShieldOverview = (clientId = null) =>
  request(clientId ? `/shield/overview?client_id=${clientId}` : '/shield/overview')

export const triggerShieldScan = (clientId) =>
  request(`/shield/scan/${clientId}`, { method: 'POST' })

// ── CloudIQ CostGuard: FinOps & Cloud Cost Optimization ─────────────────────
export const getCostGuardScan = (clientId) =>
  request(`/costguard/scan?client_id=${encodeURIComponent(clientId)}`)

export const getCostGuardHistory = (clientId, limit = 20) =>
  request(`/costguard/history?client_id=${encodeURIComponent(clientId)}&limit=${encodeURIComponent(limit)}`)

// ── Fleet Coverage & Live Log Streaming ──────────────────────────────────────
export const getFleetCoverage = (clientId) =>
  request(`/fleet/coverage/${encodeURIComponent(clientId)}`)

export const getAgentLogs = (agentId) =>
  request(`/agents/${encodeURIComponent(agentId)}/logs`)





